import { gql } from '@apollo/client';

// ============================================================================
// PHONE MUTATIONS
// Mutations for adding and verifying customer phone numbers.
// ============================================================================

/**
 * ADD_PHONE_NUMBERS_MUTATION
 *
 * Adds a phone number to the customer's account.
 *
 * @param customerId  - The customer's ID (currentCustomer.id)
 * @param description - Label for the number, e.g. "Huvudtelefonnummer"
 * @param phoneNo     - International format, e.g. "+46701234567"
 * @param phoneType   - PhoneTypeId enum: MAIN (or omit for no type)
 * @param sortOrder   - Display sort order (defaults to 1)
 *
 * @returns List of added phone numbers with id, number, verified status etc.
 */
export const ADD_PHONE_NUMBERS_MUTATION = gql`
	mutation AddPhoneNumbers(
		$customerId: Int!
		$description: String!
		$phoneNo: String!
		$phoneType: PhoneTypeId!
		$sortOrder: Byte!
	) {
		addPhoneNumbers(
			input: {
				phoneNumbers: {
					customerId: $customerId
					description: $description
					phoneNo: $phoneNo
					phoneType: $phoneType
					sortOrder: $sortOrder
				}
			}
		) {
			phoneNumber {
				description
				id
				number
				verified
				verifiedAt
			}
		}
	}
`;

// ============================================================================
// UPDATE PHONE NUMBERS MUTATION
// ============================================================================

/**
 * UPDATE_PHONE_NUMBERS_MUTATION
 *
 * Updates an existing phone number on the customer's account.
 * Also triggers a new SMS verification code to be sent to the updated number.
 *
 * @param phoneId     - The ID of the existing phone number to update
 * @param customerId  - The customer's ID (currentCustomer.id)
 * @param description - Label for the number, e.g. "Huvudtelefonnummer"
 * @param phoneNo     - International format, e.g. "+46701234567"
 * @param phoneType   - PhoneTypeId enum: MAIN
 * @param sortOrder   - Display sort order (defaults to 1)
 *
 * @returns Updated list of phone numbers with id, number, verified status etc.
 */
export const UPDATE_PHONE_NUMBERS_MUTATION = gql`
	mutation UpdatePhoneNumbers(
		$phoneId: Int!
		$customerId: Int!
		$description: String!
		$phoneNo: String!
		$phoneType: PhoneTypeId!
		$sortOrder: Byte!
	) {
		updatePhoneNumbers(
			input: {
				phones: {
					phoneId: $phoneId
					customerId: $customerId
					description: $description
					phoneNo: $phoneNo
					phoneType: $phoneType
					sortOrder: $sortOrder
				}
			}
		) {
			phoneNumber {
				description
				id
				number
				verified
				verifiedAt
			}
		}
	}
`;

/**
 * DELETE_PHONES_MUTATION
 *
 * Deletes one or more phone numbers from the customer's account.
 *
 * @param phoneNumberIds - The ID(s) of the phone number(s) to delete
 *
 * @returns phoneNumberDeletePayload containing the deleted phoneIds
 */
export const DELETE_PHONES_MUTATION = gql`
	mutation DeletePhones($phoneNumberIds: [Int!]!) {
		deletePhones(input: { phoneNumberIds: $phoneNumberIds }) {
			phoneNumberDeletePayload {
				phoneIds
			}
		}
	}
`;

/**
 * VERIFY_PHONE_NUMBER_MUTATION
 *
 * Verifies a phone number using the SMS code sent after addPhoneNumbers.
 *
 * @param phoneNumberId    - The ID of the unverified phone number (returned from addPhoneNumbers)
 * @param verificationCode - The SMS verification code entered by the user (sent as String)
 *
 * @returns The verified PhoneNumber with id, number, verified, verifiedAt and type
 */
export const VERIFY_PHONE_NUMBER_MUTATION = gql`
	mutation VerifyPhoneNumber($phoneNumberId: Int!, $verificationCode: String!) {
		verifyPhoneNumber(
			input: { phoneNumberId: $phoneNumberId, verificationCode: $verificationCode }
		) {
			phoneNumber {
				description
				id
				number
				verified
				verifiedAt
				type {
					id
					name
				}
			}
		}
	}
`;

// ============================================================================
// RESEND PHONE VERIFICATION CODE
// ============================================================================

/**
 * ADD_AND_SEND_PHONE_NUMBER_VERIFICATION_CODE_MUTATION
 *
 * Resends a verification code via SMS to a previously added (but unverified) phone number.
 *
 * @param phoneNumberId - The ID of the unverified phone number (returned from addPhoneNumbers)
 *
 * @returns The phone number object for confirmation
 */
export const ADD_AND_SEND_PHONE_NUMBER_VERIFICATION_CODE_MUTATION = gql`
	mutation AddAndSendPhoneNumberVerificationCode($phoneNumberId: Int!) {
		addAndSendPhoneNumberVerificationCode(input: { phoneNumberId: $phoneNumberId }) {
			phoneNumber {
				description
				id
				number
				verified
				verifiedAt
			}
		}
	}
`;
